import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, DropdownMenu, Search, SparkIcon, TagColored, Text, TooltipTrigger } from '@spark';
import SummarizationSampleDetail from './SummarizationSampleDetail';
import { fetchSummarizationSamples } from '@/features/evaluation/api/summarizationApi';
import SamplesFilterDropdown from './SamplesFilterDropdown';
import { isMissingGenerativeOutput, normalizeToPercent } from './summarizationUtils';
import { ModelResponsesExportButton } from '../ModelResponsesExportButton';
import { useAuth } from '@/features/auth/AuthContext';
import { TaskType } from '@/shared/domain/taskTypes';
import { isClassicMetricVisible } from './classicMetricsDefaults';
import { buildSampleTableMetricColumns } from './sampleMetricColumns';

const formatRawNumber = (n) => {
  if (n === null || n === undefined) return '';
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return String(n);
  // Show high precision without trailing zeros explosion.
  return v.toString();
};

const PROGRESS_BAR_FILL = 'var(--semantic-border-active)';
const METRIC_VALUE_COLOR = 'var(--semantic-content-tertiary)';

// Small fixed-size metric bar (80x6) matching Spark ProgressBar colors
// (active blue fill / subtle track) and prior antd Progress size=[80, 6].
const MetricBar = ({ percent }) => (
  <div style={{ width: 80, height: 6, borderRadius: 3, background: 'var(--semantic-surface-subtle)', overflow: 'hidden', flexShrink: 0 }}>
    <div
      style={{
        width: `${Math.max(0, Math.min(100, percent))}%`,
        height: '100%',
        borderRadius: 3,
        background: PROGRESS_BAR_FILL,
      }}
    />
  </div>
);

const PercentCell = ({ value }) => {
  const pct = normalizeToPercent(value);
  if (pct == null) {
    return <div style={{ minWidth: 120 }} />;
  }
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <MetricBar percent={Math.max(0, Math.min(100, pct))} />
        <TooltipTrigger text={formatRawNumber(pct)}>
          <Text variant="detail-regular" style={{ color: METRIC_VALUE_COLOR, fontSize: 12, whiteSpace: 'nowrap', wordBreak: 'keep-all', flexShrink: 0 }}>
            {pct.toFixed(0)}%
          </Text>
        </TooltipTrigger>
      </div>
    </div>
  );
};

const normalizeToUnitInterval = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return null;
  // Heuristic: if classic metrics arrive as 0..100, normalize to 0..1.
  const x = n > 1 ? n / 100 : n;
  return Math.max(0, Math.min(1, x));
};

const UnitIntervalCell = ({ value }) => {
  const v = normalizeToUnitInterval(value);
  const pct = v == null ? 0 : v * 100;
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <MetricBar percent={Math.max(0, Math.min(100, pct))} />
        <TooltipTrigger text={v == null ? '' : formatRawNumber(v)} disabled={v == null}>
          <Text variant="detail-regular" style={{ color: METRIC_VALUE_COLOR, fontSize: 12, whiteSpace: 'nowrap', wordBreak: 'keep-all', flexShrink: 0 }}>
            {v == null ? '' : v.toFixed(2)}
          </Text>
        </TooltipTrigger>
      </div>
    </div>
  );
};

const SummarizationSamples = ({
  clientId,
  currentUsecase,
  llmUsecaseId,
  version,
  setVersion,
  versionOptions,
  compareOptions = [],
  compareTarget = null,
  comparisonModelLabel,
  studentModelLabel,
  selectedLanguage = null,
  selectedDistillationEventId = null,
  classicMetricsConfig = null,
  llmJudgeEnabledById = null,
}) => {
  const { role } = useAuth();
  const isContentGeneration = currentUsecase?.task_type === TaskType.contentGeneration;
  const classicConfig = classicMetricsConfig ?? currentUsecase?.classic_metrics_config;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [selectedSample, setSelectedSample] = useState(null);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState([]);
  const [selectedModelKey, setSelectedModelKey] = useState(() => version);
  // Client-side sort state replicating the antd Table column sorter cycle
  // (click: ascend -> descend -> none) with the same numeric comparator.
  const [sortState, setSortState] = useState({ key: null, order: null });

  // Keep selection in sync when the version changes externally (e.g., Overview To: dropdown).
  useEffect(() => {
    // Only auto-sync when we are currently viewing a version (number).
    if (typeof selectedModelKey === 'number') {
      setSelectedModelKey(version);
    }
  }, [version, selectedModelKey]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const primary_model = selectedModelKey === 'oldModel' ? 'teacher' : 'student';
      const is_version_comparison = typeof compareTarget === 'number';
      const comparison_version = is_version_comparison ? compareTarget : null;
      const is_file_comparison = compareTarget === 'file';
      const res = await fetchSummarizationSamples({
        client_id: clientId,
        llm_usecase_id: llmUsecaseId,
        version,
        comparison_version,
        is_version_comparison,
        is_file_comparison,
        page: page - 1,
        page_size: pageSize,
        search,
        language: selectedLanguage,
        primary_model,
        selected_distillation_event_id: selectedDistillationEventId || null,
      });
      if (res?.success) {
        setRows(res?.data || []);
        setTotal(Number(res?.total || 0));
      } else {
        setRows([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, llmUsecaseId, version, compareTarget, page, pageSize, search, selectedModelKey, selectedLanguage, selectedDistillationEventId]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const metricKeyToLabel = useCallback((k) => {
    const s = String(k || '');
    if (!s) return '';
    // Preserve common "pretty" forms
    if (s.toLowerCase() === 'rouge_l') return 'ROUGE-L';
    if (s.toLowerCase() === 'rouge_1') return 'ROUGE-1';
    if (s.toLowerCase() === 'rouge_2') return 'ROUGE-2';
    if (s.toLowerCase() === 'bertscore') return 'BERTScore';
    if (s.toLowerCase() === 'bleu') return 'BLEU';
    if (s.toLowerCase() === 'meteor') return 'METEOR';
    if (s.toLowerCase() === 'compressionratio') return 'Compression Ratio';
    if (s.toLowerCase() === 'text_match') return 'Exact Match';
    if (s.toLowerCase() === 'min_hash') return 'Fuzzy Similarity (MinHash)';
    // Per-field dict-match scores arrive flattened as "DICT_MATCH.<field>".
    if (s.toUpperCase().startsWith('DICT_MATCH.')) {
      return `Field Match (${s.slice('DICT_MATCH.'.length)})`;
    }
    if (s.toLowerCase() === 'dict_match') return 'Field Match';
    // Default: Title Case from snake/kebab
    return s
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  const criteriaItems = useMemo(
    () =>
      isContentGeneration
        ? [
          // Align with optuneutils.parameter_validators.generative_params.GenerativeLlmAsAJudgeMetrics
          { key: 'factuality', label: 'Factuality' },
          { key: 'coherence', label: 'Coherence' },
        ]
        : [
          // Align with optuneutils.parameter_validators.summarization_params.SingleLlmAsAJudgeSummarizationMetrics
          { key: 'factuality', label: 'Factuality' },
          { key: 'completeness', label: 'Completeness' },
          { key: 'conciseness', label: 'Conciseness' },
          { key: 'coherence', label: 'Coherence' },
          { key: 'compression', label: 'Compression' },
        ],
    [isContentGeneration]
  );

  const classicItems = useMemo(
    () => {
      const items = isContentGeneration
        ? [
          // Align with optuneutils.parameter_validators.generative_params.GenerativeClassicMetrics (BLEU excluded)
          { key: 'TEXT_MATCH', label: 'Exact Match' },
          { key: 'MIN_HASH', label: 'Fuzzy Similarity (MinHash)' },
          { key: 'ROUGE_1', label: 'ROUGE-1' },
          { key: 'ROUGE_2', label: 'ROUGE-2' },
          { key: 'ROUGE_L', label: 'ROUGE-L' },
          { key: 'METEOR', label: 'METEOR' },
          { key: 'BERTScore', label: 'BERTScore' },
        ]
        : [
          // Align with optuneutils.parameter_validators.summarization_params.SummarizationClassicMetrics (BLEU excluded)
          { key: 'ROUGE_1', label: 'ROUGE-1' },
          { key: 'ROUGE_2', label: 'ROUGE-2' },
          { key: 'ROUGE_L', label: 'ROUGE-L' },
          { key: 'METEOR', label: 'METEOR' },
          { key: 'BERTScore', label: 'BERTScore' },
          { key: 'CompressionRatio', label: 'Compression Ratio' },
        ];
      return items.filter((item) => isClassicMetricVisible(item.key, classicConfig));
    },
    [isContentGeneration, classicConfig]
  );

  const sampleMetricColumns = useMemo(
    () =>
      buildSampleTableMetricColumns({
        rows,
        judgeItems: criteriaItems,
        classicItems,
        enabledById: llmJudgeEnabledById,
        classicConfig,
      }),
    [rows, criteriaItems, classicItems, llmJudgeEnabledById, classicConfig],
  );

  const judgeMetricKeys = useMemo(
    () => new Set(criteriaItems.map((i) => i.key)),
    [criteriaItems]
  );
  const classicMetricKeys = useMemo(
    () => new Set(classicItems.map((i) => i.key)),
    [classicItems]
  );

  const allMetricKeys = sampleMetricColumns.allMetricKeys;
  const customItems = useMemo(
    () => sampleMetricColumns.customKeys.map((k) => ({ key: k, label: metricKeyToLabel(k) })),
    [sampleMetricColumns.customKeys, metricKeyToLabel],
  );

  const visibleMetricKeysSet = useMemo(() => {
    const hidden = new Set(hiddenMetricKeys || []);
    return new Set((allMetricKeys || []).filter((k) => !hidden.has(k)));
  }, [allMetricKeys, hiddenMetricKeys]);

  const handleSortClick = useCallback((key) => {
    setSortState((prev) => {
      if (prev.key !== key || prev.order == null) return { key, order: 'ascend' };
      if (prev.order === 'ascend') return { key, order: 'descend' };
      return { key: null, order: null };
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!sortState.key || !sortState.order) return rows;
    const k = sortState.key;
    const sorted = [...(rows || [])].sort(
      (a, b) => (Number(a?.[k]) || 0) - (Number(b?.[k]) || 0)
    );
    if (sortState.order === 'descend') sorted.reverse();
    return sorted;
  }, [rows, sortState]);

  const getMetricRender = useCallback(
    (key) => {
      // Criteria: percent-style bars (0..100)
      if (judgeMetricKeys.has(key)) return (v) => <PercentCell value={v} />;

      // Classic metrics: values shown as 0..1 (progress bar still uses 0..100 internally)
      if (classicMetricKeys.has(key)) return (v) => <UnitIntervalCell value={v} />;

      // For unknown metrics: pick a reasonable renderer based on observed values.
      let sample = null;
      for (const r of rows || []) {
        const val = r?.[key];
        if (val === null || val === undefined || val === '') continue;
        const n = typeof val === 'number' ? val : Number(val);
        if (!Number.isFinite(n)) continue;
        sample = n;
        break;
      }

      // For classic metrics (often 0..1) and any other numeric values, render as a
      // percent bar after normalization (0..1 -> 0..100).
      return (v) => <PercentCell value={v} />;
    },
    [rows, judgeMetricKeys, classicMetricKeys]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Sample Text',
        dataIndex: 'sample_text',
        key: 'sample_text',
        width: 360,
        render: (_, record) => {
          const text = String(record.sample_text || '');
          // Show a longer preview and allow wrapping to new lines.
          // (Full text can be extremely long; keep a reasonable preview.)
          const PREVIEW_CHARS = 180;
          const truncated = text.length > PREVIEW_CHARS ? `${text.slice(0, PREVIEW_CHARS)}...` : text;
          const comparedMissing = Boolean(comparisonModelLabel)
            && isMissingGenerativeOutput(record.teacher_summary);
          const studentMissing = isMissingGenerativeOutput(record.student_summary);
          return (
            <div>
              <Text
                variant="base-regular"
                color="primary"
                as="span"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedSample(record)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setSelectedSample(record);
                }}
                style={{
                  display: 'block',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  cursor: 'pointer',
                }}
              >
                {truncated}
              </Text>
              {(comparedMissing || studentMissing) && (
                <div style={{ marginTop: 8 }}>
                  <TagColored label="No Response" variant="disabled" />
                </div>
              )}
            </div>
          );
        },
      },
    ];

    // Metric columns (Criteria + Custom), filtered by dropdown selection
    (allMetricKeys || []).forEach((k) => {
      if (!visibleMetricKeysSet.has(k)) return;
      const labelText =
        k === 'factuality'
          ? 'Factuality'
          : k === 'completeness'
            ? 'Completeness'
            : k === 'conciseness'
              ? 'Conciseness'
              : k === 'coherence'
              ? 'Coherence'
              : k === 'compression'
                ? 'Compression'
                : metricKeyToLabel(k);
      const isSorted = sortState.key === k && sortState.order != null;
      cols.push({
        title: (
          <span
            role="button"
            tabIndex={0}
            onClick={() => handleSortClick(k)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleSortClick(k);
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', userSelect: 'none' }}
          >
            {labelText}
            <SparkIcon
              slug={
                isSorted
                  ? (sortState.order === 'ascend' ? 'arrow-narrow-up' : 'arrow-narrow-down')
                  : 'arrows-sort'
              }
              size="small"
            />
          </span>
        ),
        dataIndex: k,
        key: k,
        width: 150,
        render: getMetricRender(k),
      });
    });

    return cols;
  }, [allMetricKeys, comparisonModelLabel, getMetricRender, metricKeyToLabel, visibleMetricKeysSet, sortState, handleSortClick]);

  // Options for the Model dropdown: comparison targets (teacher / results file)
  // followed by "Specific vX" version entries — same list the antd Select rendered.
  const modelSelectOptions = useMemo(() => {
    const compare = (compareOptions || [])
      // Avoid duplicate version entries: versions are rendered below as "Specific vX"
      .filter((opt) => opt?.type !== 'optune' && typeof opt?.value !== 'number')
      .map((opt) => ({ label: String(opt.label), value: opt.value }));
    const versions = (versionOptions || []).map((v) => ({
      label: Number(v) % 1 === 0 ? `Specific v${Number(v)}.0` : `Specific v${v}`,
      value: v,
    }));
    return [...compare, ...versions];
  }, [compareOptions, versionOptions]);

  const modelSelectItems = useMemo(
    () =>
      modelSelectOptions.map((opt) => ({
        label: opt.label,
        state: opt.value === selectedModelKey ? 'selected' : 'default',
      })),
    [modelSelectOptions, selectedModelKey]
  );

  const modelSelectDisplayText = useMemo(() => {
    const found = modelSelectOptions.find((opt) => opt.value === selectedModelKey);
    return found ? found.label : undefined;
  }, [modelSelectOptions, selectedModelKey]);

  if (selectedSample) {
    return (
      <SummarizationSampleDetail
        sample={selectedSample}
        taskType={currentUsecase?.task_type}
        comparisonModelLabel={comparisonModelLabel}
        studentModelLabel={studentModelLabel}
        onBack={() => setSelectedSample(null)}
      />
    );
  }

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 16,
          marginBottom: 24,
          flexWrap: 'wrap',
        }}
      >
        <div className="fs-unmask" style={{ width: 256 }}>
          <DropdownMenu
            size="medium"
            label="Model"
            showLabel
            placeholder="Model"
            text={modelSelectDisplayText}
            items={modelSelectItems}
            value={modelSelectDisplayText}
            onSelect={(item, index) => {
              const opt = modelSelectOptions[index];
              if (!opt) return;
              const v = opt.value;
              setSelectedModelKey(v);
              // If a specific version is chosen, update the shared version state.
              if (typeof v === 'number') {
                setVersion(v);
              }
              setPage(1);
            }}
          />
        </div>

        <div style={{ width: 500 }}>
          <Search
            size="medium"
            placeholder="Search Samples"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}>
          <SamplesFilterDropdown
            classicItems={sampleMetricColumns.classicItems}
            judgeItems={sampleMetricColumns.judgeItems}
            customItems={customItems}
            hiddenKeys={hiddenMetricKeys}
            onChangeHiddenKeys={setHiddenMetricKeys}
            buttonIcon={<SparkIcon slug="filter" size="small" />}
          />
          <ModelResponsesExportButton
            currentUsecase={currentUsecase}
            version={version}
            llmUsecaseId={llmUsecaseId}
            filters={{ search, language: selectedLanguage }}
            isVersionComparison={typeof compareTarget === 'number'}
            comparisonVersion={typeof compareTarget === 'number' ? compareTarget : null}
            comparisonModelName={comparisonModelLabel ?? 'Teacher Model'}
            disabled={loading}
            role={role}
            isFileComparison={compareTarget === 'file'}
          />
        </div>
      </div>

      <div className="fs-unmask" style={{ width: '100%', overflowX: 'auto' }}>
        <DataTable
          columns={columns}
          dataSource={sortedRows}
          rowKey={(r) => r.id || r.sample_text}
          loading={loading}
          pagination={{
            total,
            current: page,
            pageSize,
            pageSizeOptions: [10, 20, 50],
            onChange: (p) => {
              setPage(p);
            },
            onPageSizeChange: (ps) => {
              setPageSize(ps);
              setPage(1);
            },
          }}
        />
      </div>
    </div >
  );
};

export default SummarizationSamples;

